import {
  takeLatest,
  put,
  call,
  select,
  take,
  takeEvery
} from 'redux-saga/effects';
import { push } from 'connected-react-router';
import {
  getTemplatesIfNeeded,
  getPaginatedTemplatesIfNeeded,
  getMinimalAttachmentTemplatesIfNeeded,
  getAttachmentTemplateNamesByUuidsIfNeeded,
  getTagsIfNeeded,
  getTemplateIfNeeded,
  createTemplateFromCurrent,
  updateTemplateFromCurrent,
  deleteTemplatesByIdList,
  warnAndArchive,
  autoSaveTimerStart,
  getAttachmentTemplateFile
} from './sagas';
import {
  GET_ATTACHMENT_TEMPLATES,
  GET_PAGINATED_ATTACHMENT_TEMPLATES,
  GET_MINIMAL_ATTACHMENT_TEMPLATES,
  GET_ATTACHMENT_TEMPLATE_NAMES_BY_UUIDS,
  GET_ATTACHMENT_TEMPLATE,
  CREATE_ATTACHMENT_TEMPLATE,
  CREATING_ATTACHMENT_TEMPLATE_SUCCEEDED,
  UPDATE_ATTACHMENT_TEMPLATE,
  UPDATING_ATTACHMENT_TEMPLATE_SUCCEEDED,
  DELETE_ATTACHMENT_TEMPLATES,
  getPaginatedAttachmentTemplates,
  CREATING_ATTACHMENT_TEMPLATE_FAILED,
  UPDATING_ATTACHMENT_TEMPLATE_FAILED,
  GETTING_ATTACHMENT_TEMPLATE_FAILED,
  GETTING_ATTACHMENT_TEMPLATES_FAILED,
  GETTING_MINIMAL_ATTACHMENT_TEMPLATES_FAILED,
  DELETING_ATTACHMENT_TEMPLATES_FAILED,
  WARN_AND_ARCHIVE_ATTACHMENT_TEMPLATE,
  GETTING_ASSOCIATED_CAMPAIGNS_FOR_ATTACHMENT_TEMPLATE_FAILED,
  ATTACHMENT_AUTO_SAVE_TIMER_START,
  GET_ATTACHMENT_TAGS,
  DELETING_ATTACHMENT_TEMPLATES_SUCCEEDED,
  SET_CURRENT_ATTACHMENT_TEMPLATE,
  GETTING_ATTACHMENT_TEMPLATE_SUCCEEDED,
  GET_ATTACHMENT_TEMPLATE_FILE
} from './actions';
import { actions as UserActions } from '../user';
import { actions as userMessaging } from '../userMessaging';
import { actions as SettingsActions } from '../settings';
import { sagas as SettingsSaga } from '../settings';

/* Helper Sagas */
function* forceGetAllTemplates(action) {
  yield put(getPaginatedAttachmentTemplates({ force: true }));
  const isInTemplateDesignDialog = yield select(
    store => store.attachmentTemplate.isInTemplateDesignDialog
  );
  const currentAttachmentTemplate = yield select(
    store => store.attachmentTemplate.current
  );

  if (
    action?.type !== 'DELETING_ATTACHMENT_TEMPLATES_SUCCEEDED' &&
    !isInTemplateDesignDialog
  ) {
    if (currentAttachmentTemplate.uuid) {
      if(window.self !== window.top) {
        yield put(push(`/unlayerAttachmentEditor/${currentAttachmentTemplate.uuid}`));
      } else {
        yield put(push(`/attachment-templates/design/${currentAttachmentTemplate.uuid}`));
      }
    }
  }
}
function* waitForUserReady() {
  const userReady = yield select(store => store.user.ready);
  if (!userReady) {
    yield take(UserActions.LOGIN_READY);
  }
}

function* HandleFailuresSaga() {
  yield takeLatest(
    [
      CREATING_ATTACHMENT_TEMPLATE_FAILED,
      UPDATING_ATTACHMENT_TEMPLATE_FAILED,
      GETTING_ATTACHMENT_TEMPLATE_FAILED,
      GETTING_ATTACHMENT_TEMPLATES_FAILED,
      GETTING_MINIMAL_ATTACHMENT_TEMPLATES_FAILED,
      DELETING_ATTACHMENT_TEMPLATES_FAILED,
      GETTING_ASSOCIATED_CAMPAIGNS_FOR_ATTACHMENT_TEMPLATE_FAILED
    ],
    function*(action) {
      yield put(
        userMessaging.setMessageForFailedAction({
          type: action.type,
          error: action.payload
        })
      );
    }
  );
}

/* APP FLOW - Templates */
function* AttachmentTemplateGetManySaga() {
  yield takeLatest(GET_ATTACHMENT_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getTemplatesIfNeeded, action);
  });
}
function* PaginatedAttachmentTemplateGetManySaga() {
  yield takeLatest(GET_PAGINATED_ATTACHMENT_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getPaginatedTemplatesIfNeeded, action);
  });
}
function* MinimalAttachmentTemplateGetManySaga() {
  yield takeLatest(GET_MINIMAL_ATTACHMENT_TEMPLATES, function*(action) {
    yield call(waitForUserReady);
    yield call(getMinimalAttachmentTemplatesIfNeeded, action);
  });
}

function* AttachmentTemplateNameHydrationSaga() {
  yield takeLatest(GET_ATTACHMENT_TEMPLATE_NAMES_BY_UUIDS, function*(action) {
    yield call(waitForUserReady);
    yield call(getAttachmentTemplateNamesByUuidsIfNeeded, action);
  });
}
function* TagGetSaga() {
  yield takeLatest(GET_ATTACHMENT_TAGS, function*(action) {
    yield call(getTagsIfNeeded, action);
  });
}
function* AttachmentTemplateGetSingleSaga() {
  yield takeLatest(GET_ATTACHMENT_TEMPLATE, function*(action) {
    yield call(waitForUserReady);
    yield call(getTemplateIfNeeded, action);
  });
}

// brutally refresh to template list on any modification - this *could* be made to act just on the in-memory reducer instead.
function* AttachmentTemplateCreateSaga() {
  yield takeLatest(CREATE_ATTACHMENT_TEMPLATE, createTemplateFromCurrent);
}
function* AttachmentTemplateCreateSuccessSaga() {
  yield takeLatest(
    CREATING_ATTACHMENT_TEMPLATE_SUCCEEDED,
    forceGetAllTemplates
  );
}
function* AttachmentTemplateUpdateSaga() {
  yield takeLatest(UPDATE_ATTACHMENT_TEMPLATE, updateTemplateFromCurrent);
}
function* AttachmentTemplateUpdateSuccessSaga() {
  yield takeLatest(
    UPDATING_ATTACHMENT_TEMPLATE_SUCCEEDED,
    forceGetAllTemplates
  );
}

function* WarnAndArchiveAttachmentTemplate() {
  yield takeLatest(WARN_AND_ARCHIVE_ATTACHMENT_TEMPLATE, warnAndArchive);
}

function* AttachmentTemplateDeleteSaga() {
  yield takeLatest(DELETE_ATTACHMENT_TEMPLATES, function*(action) {
    yield call(deleteTemplatesByIdList, action);
    // yield call(forceGetAllTemplates); // force refresh even if the delete is unsuccessful - since we don't know if we only partially failed.
  });
}

function* AttachmentTemplateDeleteSuccessSaga() {
  yield takeLatest(
    DELETING_ATTACHMENT_TEMPLATES_SUCCEEDED,
    forceGetAllTemplates
  );
}

function* AutoSaveTimerSaga() {
  yield takeEvery(ATTACHMENT_AUTO_SAVE_TIMER_START, function*(action) {
    yield call(autoSaveTimerStart, action);
  });
}

function* HandleTemplateLanguageChangeSaga() {
  yield call(SettingsSaga.waitForGetAllMetaData);
  const availableLanguages = yield select(store => store.settings.availableLanguages);
  const settingsMetaData = yield select(store => store.settings.settingsMetaData.data);
  yield takeLatest([
    SET_CURRENT_ATTACHMENT_TEMPLATE,
    GETTING_ATTACHMENT_TEMPLATE_SUCCEEDED
  ], function*(action) {
    let languageCode;
    if (action.type === SET_CURRENT_ATTACHMENT_TEMPLATE) {
      languageCode = action.payload?.languageCode;
    } else if (action.type === GETTING_ATTACHMENT_TEMPLATE_SUCCEEDED) {
      languageCode = action.payload?.template?.languageCode;
    }
    // Fall back to settingsMetaData.languageCode if template doesn't have one
    if (!languageCode) {
      languageCode = settingsMetaData?.languageCode;
    }
    if (languageCode && availableLanguages.length > 0) {
      yield put(SettingsActions.updateTemplateLanguagePreference(availableLanguages.filter(language => language.languageCode === languageCode)[0]));
    }
  });
}

function* GetAttachmentTemplateFileSaga() {
  yield takeLatest(GET_ATTACHMENT_TEMPLATE_FILE, getAttachmentTemplateFile);
}

export default [
  AttachmentTemplateGetManySaga,
  PaginatedAttachmentTemplateGetManySaga,
  MinimalAttachmentTemplateGetManySaga,
  TagGetSaga,
  AttachmentTemplateGetSingleSaga,
  AttachmentTemplateCreateSaga,
  AttachmentTemplateCreateSuccessSaga,
  AttachmentTemplateUpdateSaga,
  AttachmentTemplateUpdateSuccessSaga,
  AttachmentTemplateDeleteSaga,
  AttachmentTemplateDeleteSuccessSaga,
  HandleFailuresSaga,
  WarnAndArchiveAttachmentTemplate,
  AutoSaveTimerSaga,
  HandleTemplateLanguageChangeSaga,
  GetAttachmentTemplateFileSaga,
  AttachmentTemplateNameHydrationSaga
];
